// Utils & Constants
import {
  DATE_FORMATS,
  DurationConstants,
} from "../../constants/GeneralConstants";
import { getDifferenceInDays } from "../../common-utils/date-utils/DateUtils";
import { calEstPrice } from "./PriceFactorUtil";

export function getOtsAndCostDetails(roadInfo = {}, cityPlan = {}, duration) {
  const { id: roadId } = roadInfo || {};
  const { targetGroupId, roadStretchOtsMap = {} } = cityPlan || {};
  const roadOtsObject = targetGroupId ? roadStretchOtsMap[roadId] : roadInfo;

  if (!roadOtsObject) {
    return { estTgSpecific: "", estGeneric: "", estTotal: "" };
  }

  const {
    genericOts,
    genericOtsLit,
    targetOts,
    targetOtsLit,
    otsAvg,
    otsLitAvg,
  } = roadOtsObject || {};

  const estTotalWhenTgNotSelected =
    ((otsLitAvg ?? otsAvg) / DurationConstants.THIRTY_DAYS) * duration;

  const estTgSpecific = (targetOtsLit ?? targetOts) * duration;
  const estGeneric = (genericOtsLit ?? genericOts) * duration;

  const estTotal =
    estTgSpecific && estGeneric
      ? estTgSpecific + estGeneric
      : estTotalWhenTgNotSelected;

  return { estTgSpecific, estGeneric, estTotal };
}

// idToObjectMap ==> roadIdToStretchMap or segmentMap
function mapMediaToSegmentOrStretchIds(roadIdToMediaMap, idToRoadMap) {
  return Object.keys(idToRoadMap).reduce((acc, eachRoadId) => {
    // Check if the media exists with the roadId
    if (!roadIdToMediaMap[eachRoadId]) {
      return acc;
    }

    const stretchId = idToRoadMap[eachRoadId].id;
    const roadMediaArray = acc[stretchId] || [];
    const newRoadMediaArray = roadMediaArray.concat(
      roadIdToMediaMap[eachRoadId]
    );

    acc[stretchId] = newRoadMediaArray;
    return acc;
  }, {});
}

function constructRoadIdToStretchMap(stretchMap) {
  if (Object.values(stretchMap).length === 0) {
    return {};
  }

  return Object.values(stretchMap).reduce((acc, eachStretch) => {
    const roadIds = eachStretch["roadSegmentIds"];
    const eachRoadIdToObjectMap = roadIds.reduce((idAcc, eachRoadId) => {
      idAcc[eachRoadId] = eachStretch;
      return idAcc;
    }, {});
    acc = { ...acc, ...eachRoadIdToObjectMap };
    return acc;
  }, {});
}

function constructRoadIdToMediaMap(mediaDetails) {
  if (mediaDetails.length === 0) {
    return {};
  }

  const roadIdToMediaMap = {};
  for (const eachMedia of mediaDetails) {
    const roadIds = eachMedia["roadIds"];

    for (const eachRoadId of roadIds) {
      const roadMediaArr = roadIdToMediaMap[eachRoadId] || [];

      // Add current media
      roadMediaArr.push(eachMedia);
      roadIdToMediaMap[eachRoadId] = roadMediaArr;
    }
  }
  return roadIdToMediaMap;
}

function includeRoadWithEmptyMedia(roadMap, roadIdToMediaListMap) {
  // Check for the roads with no Media
  const stretchIdToEmptyMediaListMap = Object.keys(roadMap).reduce(
    (acc, roadKey) => {
      if (roadIdToMediaListMap[roadKey]) {
        return acc;
      }
      acc[roadKey] = [];
      return acc;
    },
    {}
  );

  return {
    ...roadIdToMediaListMap,
    ...stretchIdToEmptyMediaListMap,
  };
}

export function mapMediaToSegmentsAndStretches(
  mediaDetails,
  segmentMap,
  stretchMap
) {
  let stretchIdToMediaListMap = {};
  let segmentIdToMediaListMap = {};

  if (Object.keys(segmentMap) == 0 && Object.keys(stretchMap).length == 0) {
    return { stretchIdToMediaListMap, segmentIdToMediaListMap };
  }
  // Construct roadIdToMediaMap
  const roadIdToMediaMap = constructRoadIdToMediaMap(mediaDetails);

  // Construct roadIdsToStretchMap
  const roadIdToStretchMap = constructRoadIdToStretchMap(stretchMap);

  // Map to store the Stretch Id to the MediaList
  stretchIdToMediaListMap = mapMediaToSegmentOrStretchIds(
    roadIdToMediaMap,
    roadIdToStretchMap
  );

  // include stretches with no Media
  stretchIdToMediaListMap = includeRoadWithEmptyMedia(
    stretchMap,
    stretchIdToMediaListMap
  );

  // Map to store the Segment Id to the MediaList
  segmentIdToMediaListMap = mapMediaToSegmentOrStretchIds(
    roadIdToMediaMap,
    segmentMap
  );

  // include Segments with no Media
  segmentIdToMediaListMap = includeRoadWithEmptyMedia(
    segmentMap,
    segmentIdToMediaListMap
  );

  return { stretchIdToMediaListMap, segmentIdToMediaListMap };
}

/**
 *
 * @param {*} campaign
 * @returns
 * @deprecated
 */
export function constructDateString(campaign) {
  if (!campaign || !campaign.startDate || !campaign.endDate) {
    return "XXXX-XXXX";
  }

  return `${campaign.startDate.format(
    DATE_FORMATS.full_month_with_date_year
  )} - ${campaign.endDate.format(DATE_FORMATS.full_month_with_date_year)}`;
}

export function constructTotalCost(campaign, tgOts, genericOts) {
  if (!campaign || !campaign.tgCPM || !campaign.genericCPM) {
    return "XXXXX";
  }
  return tgOts * campaign.tgCPM + genericOts * campaign.genericCPM;
}

export function getStretchImpressions(stretchInfo) {
  // TODO: Backend is sending in-consistent keys in different API's
  // some API's as ots and otsLit, and in some API's otsAvg, otsLitAvg..
  const {
    ots,
    otsLit,
    otsAvg,
    otsLitAvg,
    genericOtsLit,
    genericOts,
    targetOts,
    targetOtsLit,
  } = stretchInfo;

  const estImpressions = otsLitAvg ?? otsLit ?? ots ?? otsAvg ?? 0;

  // for TG-specific-impressions
  const genericImpressions = genericOtsLit ?? genericOts ?? 0;
  const specificImpressions = targetOtsLit ?? targetOts ?? 0;

  return {
    genericImpressions: genericImpressions
      ? genericImpressions
      : estImpressions,
    specificImpressions,
  };
}

/**
 *
 * @param {*} roadStretchOtsMap  ==> this has the selected roadStretchInfo (Impressions)
 * @param {*} roadStretchMap  ==> this has the mop and occupancy info
 * @param {*} dayCount
 * @param {*} tgPriceFactor
 * @param {*} genPriceFactor
 * @returns
 */
export function constructTotalImpAndCost1(
  roadStretchOtsMap,
  roadStretchMap,
  dayCount,
  tgPriceFactor,
  genPriceFactor
) {
  if (Object.keys(roadStretchMap).length < 1) {
    return {
      genericImpressions: 0,
      specificImpressions: 0,
      specificCost: 0,
      genericCost: 0,
    };
  }

  const totalOtsAndCost = Object.keys(roadStretchOtsMap).reduce(
    (acc, eachStretchId) => {
      const eachStretchOts = roadStretchOtsMap[eachStretchId];
      const { genericImpressions, specificImpressions } =
        getStretchImpressions(eachStretchOts);

      // Total Impressions..
      if (genericImpressions) {
        acc.genericImpressions = genericImpressions + acc.genericImpressions;
      }
      if (specificImpressions) {
        acc.specificImpressions = acc.specificImpressions + specificImpressions;
      }

      // Calculation of EstPrice
      const { minOperatingPriceAvg = "", occupancyAvg = "" } =
        roadStretchMap[eachStretchId];
      if (minOperatingPriceAvg && occupancyAvg) {
        const estimatePriceBean = {
          genericImpressions,
          specificImpressions,
          mop: minOperatingPriceAvg,
          occupancy: occupancyAvg,
          dayCount,
          tgPriceFactor,
          genPriceFactor,
        };
        const { genericCost, specificCost } = calEstPrice(estimatePriceBean);
        acc.genericCost = acc.genericCost + genericCost;
        acc.specificCost = specificCost
          ? acc.specificCost + specificCost
          : acc.specificCost;
      }

      return acc;
    },
    {
      genericImpressions: 0,
      specificImpressions: 0,
      specificCost: 0,
      genericCost: 0,
    }
  );
  totalOtsAndCost.genericImpressions =
    totalOtsAndCost.genericImpressions * dayCount;

  totalOtsAndCost.specificImpressions =
    totalOtsAndCost.specificImpressions * dayCount;

  return totalOtsAndCost;
}

export function constructTotalImpAndCost(
  selectedRoadStretches = {},
  roadStretchOtsMap = {},
  dayCount,
  tgPriceFactor,
  genPriceFactor
) {
  if (Object.keys(selectedRoadStretches).length < 1) {
    return {
      genericImpressions: 0,
      specificImpressions: 0,
      specificCost: 0,
      genericCost: 0,
      avgMop: 0,
    };
  }

  const totalOtsAndCost = Object.keys(selectedRoadStretches).reduce(
    (acc, eachStretchId) => {
      const eachStretchOts = roadStretchOtsMap[eachStretchId] || {};
      const { avgMOP = 0 } = eachStretchOts || {};
      const { genericImpressions, specificImpressions } =
        getStretchImpressions(eachStretchOts) || {};

      // Total Impressions..
      if (genericImpressions) {
        acc.genericImpressions = genericImpressions + acc.genericImpressions;
      }
      if (specificImpressions) {
        acc.specificImpressions = acc.specificImpressions + specificImpressions;
      }

      // avg MOP
      acc.avgMOP = acc.avgMOP + avgMOP;

      // Calculation of EstPrice
      const { minOperatingPriceAvg = "", occupancyAvg = "" } =
        selectedRoadStretches[eachStretchId] || {};

      if (minOperatingPriceAvg && occupancyAvg) {
        const estimatePriceBean = {
          genericImpressions,
          specificImpressions,
          mop: minOperatingPriceAvg,
          occupancy: occupancyAvg,
          dayCount,
          tgPriceFactor,
          genPriceFactor,
        };

        const { genericCost, specificCost } =
          calEstPrice(estimatePriceBean) || {};

        acc.genericCost = acc.genericCost + genericCost;
        acc.specificCost = specificCost
          ? acc.specificCost + specificCost
          : acc.specificCost;
      }

      return acc;
    },
    {
      genericImpressions: 0,
      specificImpressions: 0,
      specificCost: 0,
      genericCost: 0,
      avgMOP: 0,
    }
  );
  totalOtsAndCost.genericImpressions =
    totalOtsAndCost.genericImpressions * dayCount;

  totalOtsAndCost.specificImpressions =
    totalOtsAndCost.specificImpressions * dayCount;

  return totalOtsAndCost;
}

export function extractZoneIds(data = {}) {
  const zoneIds = [];

  Object.keys(data).forEach((city) => {
    data[city].forEach((item) => {
      zoneIds.push(item.zoneId);
    });
  });

  return zoneIds;
}

/**
 * get Stretches Ots Split
 */
export function getStretchesTotalOtsSplit(roadStretchOtsMap = {}) {
  // if no stretches present, return default
  if (Object.keys(roadStretchOtsMap).length === 0) {
    return { ots: 0, targetOts: 0 };
  }

  // only required data
  const stretchOtsData = Object.values(roadStretchOtsMap).map((eachStretch) => {
    const { ots, otsLit, targetOts, targetOtsLit } = eachStretch;
    const finalOts = otsLit ?? ots ?? 0;
    const finalTargetOts = targetOtsLit ?? targetOts ?? 0;
    return { ots: finalOts, targetOts: finalTargetOts };
  });

  // sum all ots & tgOts for all stretches
  const stretchesTotalOts = stretchOtsData.reduce(
    (acc, curr) => {
      Object.entries(curr).forEach(([key, value]) => {
        acc[key] = (acc[key] || 0) + (value || 0);
      });
      return { ...acc };
    },
    { ots: 0, targetOts: 0 }
  );

  return stretchesTotalOts;
}

// construct Tg Heatmap Key
export function constructTgHeatmapKey(
  selectedTgAttributesMap = {},
  selectedCityId = ""
) {
  const selectedTgAttributes = [];

  // append into one array
  Object.keys(selectedTgAttributesMap).forEach((key) => {
    const tgAttributes = selectedTgAttributesMap[key];
    selectedTgAttributes.push(...tgAttributes);
  });

  // create heatmapKey with name of all tgAttributes
  const tgAttrNamesArray = selectedTgAttributes.map(
    (tgAttr) => tgAttr.tgAttrName
  );

  const joinedTgAttrNames = tgAttrNamesArray.join();
  const heatmapKeyWithCityId = `${selectedCityId}-${joinedTgAttrNames}`;

  // final heatmap key
  const heatmapKey = selectedCityId ? heatmapKeyWithCityId : joinedTgAttrNames;
  return heatmapKey;
}

/**
 * get Total Ots Split For Stretches Based On Selected Medias
 */
export function getTotalOtsSplitForStretchesBasedOnSelectedMedias(
  planStretchesOtsMap = {},
  planStretchesDetailsMap = {},
  mediaList = []
) {
  // if no media selected to plan
  if (mediaList.length === 0) {
    return { ots: 0, tgOts: 0 };
  }

  // selected media ids
  const selectedMediaIds = mediaList.map((media) => media.mediaId);

  // calculating stretch ots
  const stretchesTotalOtsData = Object.keys(planStretchesDetailsMap).map(
    (stretchId) => {
      const stretchDetails = planStretchesDetailsMap[stretchId];
      const { orgMedias = [] } = stretchDetails;

      // find selected media count from each stretch
      const stretchMediaIds = orgMedias.map((media) => media.id);
      const selectedMediaCountForStretch = selectedMediaIds.reduce(
        (acc, curr) => acc + stretchMediaIds.includes(curr),
        0
      );

      // each stretch ots data
      const stretchOtsData = planStretchesOtsMap[stretchId];
      const { ots, otsLit, targetOts, targetOtsLit } = stretchOtsData;

      // ots, tgOts for selected media count for stretch
      const finalOts = (otsLit ?? ots) * selectedMediaCountForStretch;
      const finalTargetOts =
        (targetOtsLit ?? targetOts) * selectedMediaCountForStretch;

      return { ots: finalOts, targetOts: finalTargetOts };
    }
  );

  // sum all ots & tgOts for all stretches
  const stretchesTotalOts = stretchesTotalOtsData.reduce((acc, curr) => {
    Object.entries(curr).forEach(([key, value]) => {
      acc[key] = acc[key] + value || value;
    });
    return { ...acc };
  }, {});

  return stretchesTotalOts;
}

// get Digital Media Spot Count
export function getDigitalMediasSpotCount(mediaList) {
  // if no media present, return zero
  if (!mediaList || mediaList.length === 0) {
    return 0;
  }

  // total spot count
  const totalSpotsPerSlot = mediaList.reduce((total, media) => {
    const { isDigital, noOfSpotsPerSlot } = media;

    // adding only digital media spots
    if (isDigital) {
      return total + (noOfSpotsPerSlot || 0);
    }

    // else return same total
    return total;
  }, 0);

  return totalSpotsPerSlot;
}

// get selected city name from campaign
export function getSelectedCityName(campaign = {}, cityId) {
  if (!cityId) {
    return "";
  }

  const infoByCity = campaign?.infoByCity || {};
  const cityInfo = infoByCity[cityId] || {};
  return cityInfo.cityName;
}

// get Initial Date And Duration
export function getInitialDateAndDuration(startTimestamp, endTimestamp) {
  if (!startTimestamp && !endTimestamp) {
    return { startDateObj: "", duration: "", endDateObj: "" };
  }

  const duration = getDifferenceInDays(startTimestamp, endTimestamp) - 1;

  return {
    startDateObj: new Date(startTimestamp),
    endDateObj: new Date(endTimestamp),
    duration,
  };
}

// checking is campaign is digital or not from overview response
export function checkIsDigitalCampaign(campaignPlanOverview) {
  if (!campaignPlanOverview || Object.keys(campaignPlanOverview).length === 0) {
    return false;
  }

  // data
  const { planByCity = {} } = campaignPlanOverview || {};
  const firstCityInfo = Object.values(planByCity)?.[0] || {};
  const { isDigital = false } = firstCityInfo || {};

  // return
  return isDigital;
}
